import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { PaymentForm } from "@/components/payment/PaymentForm";
import { BillingForm } from "@/components/payment/BillingForm";
import PaymentSummary from "@/components/payment/PaymentSummary";
import { BookingState } from "@/types/bookinghome";
import { initiatePayment } from "@/utils/endUserAppApi";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('public', { keyPrefix: 'payment' });
  
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>();
  const bookingState = location.state?.bookingState as BookingState;

  useEffect(() => {
    console.log('Selected Time Slot:', bookingState?.selectedTimeSlot);
    console.log('Selected Date:', bookingState?.selectedDate);
    if (!bookingState?.selectedTimeSlot || !bookingState?.selectedDate) {
      toast({
        title: t('error.invalidAccess.title'),
        description: t('error.invalidAccess.description'),
        variant: "destructive",
      });
      navigate('/');
      return;
    }

    const initializePayment = async () => {
      if (!showPaymentForm) return;
      
      try {
        const response = await initiatePayment({
          bookingId: bookingState.bookingId || '',
          amount: calculateTotal(),
          paymentMethod: 'stripe'
        });
        setClientSecret(response.clientSecret);
      } catch (error) {
        toast({
          title: t('error.initialization.title'),
          description: t('error.initialization.description'),
          variant: "destructive",
        });
      }
    };

    initializePayment();
  }, [bookingState, showPaymentForm, t, toast, navigate]);

  const calculateTotal = () => {
    const { selectedTimeSlot, participants } = bookingState;
    return (
      (participants.adults * (selectedTimeSlot?.price?.adult?.amount || 0)) +
      (participants.children * (selectedTimeSlot?.price?.child?.amount || 0)) +
      (participants.students * (selectedTimeSlot?.price?.student?.amount || 0))
    );
  };

  const handlePaymentSuccess = () => {
    // Handle successful payment
    navigate(`/confirmation/${bookingState.bookingId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{t('header.title')}</h1>
          <p className="text-muted-foreground">
            {t('header.subtitle', { activityId: bookingState.selectedTimeSlot?.id })}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {!showPaymentForm ? (
              <BillingForm 
                onSubmit={() => setShowPaymentForm(true)}
                isProcessing={isProcessing}
              />
            ) : clientSecret ? (
              <Elements 
                stripe={stripePromise} 
                options={{
                  clientSecret,
                  appearance: { theme: 'stripe' }
                }}
              >
                <PaymentForm 
                  amount={calculateTotal()}
                  bookingId={bookingState.bookingId || ''}
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                  onSuccess={handlePaymentSuccess}
                  onError={(message) => {
                    toast({
                      title: t('error.processing.title'),
                      description: message,
                      variant: "destructive",
                    });
                  }}
                />
              </Elements>
            ) : (
              <div>{t('status.initializing')}</div>
            )}
          </div>
          <div className="lg:col-span-1">
            <PaymentSummary 
              bookingState={bookingState}
              total={calculateTotal()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;